import imageCompression from "browser-image-compression";
import { Field, Form, Formik } from "formik";
import { useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { useIntl } from "react-intl";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { ImageCompressConfig } from "../../../constants/ImageCompressConfig";
import { ApiStatusTypeEnum } from "../../../enumeration/ApiStatusTypeEnum";
import { useAuth } from "../../auth/components/AuthInit";
import { TextArea } from "../../common/control/InputField";
import { getCookie } from "../../slidebar/components/DarkMode";
import * as actions from "../redux/MessageBoxAction";

const Avatar = ({ src, username, size = 33 }) => {
  const [imageError, setImageError] = useState(false);
  const fallback = username ? username.charAt(0).toUpperCase() : "?";

  const avatarStyle = {
    width: `${size}px`,
    height: `${size}px`,
    fontSize: `${size / 2}px`,
  };

  return src && !imageError ? (
    <img
      src={src}
      alt={username}
      className="rounded-circle object-fit-cover"
      style={avatarStyle}
      onError={() => setImageError(true)}
    />
  ) : (
    <div
      className="bg-secondary text-white d-flex justify-content-center align-items-center rounded-circle fw-bold text-uppercase"
      style={avatarStyle}
    >
      {fallback}
    </div>
  );
};

export const MessageBoxDetail = ({ inboxId, setInboxId, setSwalProps }) => {
  const { auth } = useAuth();
  const intl = useIntl();
  const dispatch = useDispatch();
  const [mode, setMode] = useState(getCookie("selectedTheme"));
  const formikRef = useRef();
  const fileInputRef = useRef();
  const [file, setFile] = useState([]);
  const [imagePreview, setImagePreview] = useState([]);
  const size = 40;

  const messageBoxState = useSelector(
    ({ messageBox }) => messageBox,
    shallowEqual
  );

  const handleClose = () => setInboxId(undefined);

  useEffect(() => {
    const handle = () => {
      setMode((prev) => (prev === "dark" ? "light" : "dark"));
    };

    window.addEventListener("setTheme", handle);

    return () => {
      window.removeEventListener("setTheme", handle);
    };
  }, []);

  useEffect(() => {
    if (inboxId) {
      dispatch(
        actions.getInboxDetail({ action: "inboxdetail", inboxid: inboxId })
      );
      return () => {
        dispatch(actions.getMemberInboxList({ action: "memberinboxlist" }));
        dispatch(actions.clearInboxDetail());
      };
    }
  }, [inboxId]);

  useEffect(() => {
    if (inboxId) {
      setFile([]);
      setImagePreview([]);
      formikRef.current?.resetForm();
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }, [inboxId]);

  const handleFileChange = (event) => {
    const handleFile = (file, fileName) => {
      const newFile = new File([file], fileName, {
        type: file.type,
      });
      setImagePreview((prev) =>
        prev.length < 3 ? [...prev, URL.createObjectURL(newFile)] : prev
      );
      setFile((prev) => (prev.length < 3 ? [...prev, newFile] : prev));
    };

    const fileName = event.currentTarget.files[0].name;
    imageCompression(event.currentTarget.files[0], ImageCompressConfig)
      .then((res) => handleFile(res, fileName))
      .catch(() => handleFile(event.currentTarget.files[0], fileName));
  };

  const createComment = (values) => {
    const imagenameList = file
      ? file.map(
          (f, index) => `${Date.now() + index}.${f.name.split(".").at(-1)}`
        )
      : [];

    const newQueryParams = {
      action: "addinboxcomment",
      agentid: auth.selagentid ?? auth.agentid,
      loginid: auth.selloginid ?? auth.loginid,
      projectid: auth.selprojectid ?? auth.projectid,
      inboxid: inboxId,
      attchjson: file
        ? imagenameList.map((imagename) => ({ imagename }))
        : undefined,
      ...values,
    };

    const formData = new FormData();
    formData.append("imagenameList", JSON.stringify(imagenameList));
    file.forEach((f) => {
      formData.append("imageList[]", f);
    });
    formData.append("jsonData", JSON.stringify(newQueryParams));
    dispatch(actions.createComment(formData)).then((resp) => {
      resp = resp.data;

      if (parseInt(resp.code) === ApiStatusTypeEnum.SUCCESS) {
        setFile([]);
        setImagePreview([]);
        formikRef.current?.resetForm();
        if (fileInputRef.current) fileInputRef.current.value = "";
        dispatch(
          actions.getInboxDetail({ action: "inboxdetail", inboxid: inboxId })
        );
      } else {
        setSwalProps({
          show: true,
          icon: "error",
          title: intl.formatMessage({ id: "error" }),
          text: resp.message,
        });
      }
    });
  };

  const handleDelete = (itemid, e) => {
    e.stopPropagation();
    setSwalProps({
      show: true,
      icon: "question",
      title: intl.formatMessage({ id: "confirmation" }),
      text: intl.formatMessage({ id: "questionRemoveSelectedComment" }),
      showCancelButton: true,
      onConfirm: () => {
        dispatch(
          actions.deleteInbox({
            action: "deleteinboxcomment",
            inboxid: inboxId,
            itemid,
          })
        ).then((resp) => {
          resp = resp.data;
          if (parseInt(resp.code) === ApiStatusTypeEnum.SUCCESS) {
            dispatch(
              actions.getInboxDetail({
                action: "inboxdetail",
                inboxid: inboxId,
              })
            );
            setSwalProps({
              show: true,
              icon: "success",
              title: intl.formatMessage({ id: "success" }),
              text: intl.formatMessage({ id: "removeCommentSuccessfully" }),
            });
          } else {
            setSwalProps({
              show: true,
              icon: "error",
              title: intl.formatMessage({ id: "error" }),
              text: resp.message,
            });
          }
        });
      },
    });
  };

  return (
    <Modal
      show={!!inboxId}
      onHide={handleClose}
      centered
      data-bs-theme={mode}
      size="lg"
    >
      <Modal.Header closeButton>
        <Modal.Title>{messageBoxState.inboxDetail?.title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {messageBoxState.inboxDetail && (
          <div>
            <div className="d-flex flex-column gap-2 border rounded py-2 px-3 mb-2 ">
              {messageBoxState.inboxDetail.imageurl !== "" && (
                <img
                  src={messageBoxState.inboxDetail.imageurl}
                  className="w-100"
                />
              )}
              <div className="fw-semibold fs-5">
                {messageBoxState.inboxDetail?.title}
              </div>
              <div style={{ whiteSpace: "pre-line" }}>
                {messageBoxState.inboxDetail?.content}
              </div>
            </div>
            <div
              className="d-flex flex-column gap-2"
              style={{ maxHeight: "70vh", overflowY: "auto" }}
              onScroll={(e) => {
                const target = e.target;
                const isAtBottom =
                  target.scrollHeight - target.scrollTop <=
                  target.clientHeight + 5;
                if (
                  isAtBottom &&
                  (messageBoxState.inboxDetail.currentpage ?? "1") !==
                    (messageBoxState.inboxDetail.totalpage ?? "1") &&
                  parseInt(messageBoxState.inboxDetail.totalpage ?? "1") > 1 &&
                  !messageBoxState.listLoading
                ) {
                  dispatch(
                    actions.getMoreInboxDetail({
                      action: "inboxcommentloadmore",
                      inboxid: inboxId,
                      pageview:
                        parseInt(messageBoxState.inboxDetail.currentpage) + 1,
                    })
                  );
                }
              }}
            >
              <div
                className="small px-2 border-top pt-2"
                style={{ color: "gray" }}
              >
                {intl.formatMessage({ id: "comments" })}
              </div>
              {messageBoxState.inboxDetail.inboxDetails.length === 0 && (
                <div className="p-2">
                  {intl.formatMessage({ id: "noCommentFound" })}
                </div>
              )}

              {messageBoxState.inboxDetail.inboxDetails.map((inboxDetail) => (
                <div
                  key={inboxDetail.itemid}
                  className="row border-bottom pb-1 mx-2"
                >
                  <div className="col-md-3 d-flex gap-2 align-items-center mb-1">
                    <Avatar
                      username={inboxDetail.profilename}
                      src={inboxDetail.profileimg}
                    />
                    <div>
                      <div className="fw-bold">{inboxDetail.profilename}</div>
                      <div style={{ color: "gray", fontSize: "12px" }}>
                        {inboxDetail.lastdate}
                      </div>
                    </div>
                  </div>
                  <div className="col-md-9 d-flex">
                    <div className="flex-fill">
                      {inboxDetail.inboxAttch.length > 0 && (
                        <div className="d-flex mb-1 gap-2">
                          {inboxDetail.inboxAttch.map((inboxAttch, index) => (
                            <div
                              style={{
                                width: "6vw",
                                height: "6vh",
                                overflow: "hidden",
                                flexShrink: 0,
                              }}
                              key={index}
                            >
                              <img
                                src={inboxAttch.attchurl}
                                style={{
                                  objectFit: "cover", // keep aspect ratio, no stretching
                                  display: "block",
                                }}
                              />
                            </div>
                          ))}
                        </div>
                      )}
                      <div>{inboxDetail.content}</div>
                    </div>
                    <i
                      title={intl.formatMessage({ id: "deleteComment" })}
                      className="bi bi-trash3 text-danger"
                      style={{ cursor: "pointer" }}
                      onClick={(e) => handleDelete(inboxDetail.itemid, e)}
                    />
                  </div>
                </div>
              ))}
              {messageBoxState.listLoading && (
                <div
                  className="d-flex align-items-center justify-content-center"
                  style={{ height: "40px" }}
                >
                  <div class="spinner-border" role="status">
                    <span class="visually-hidden"></span>
                  </div>
                </div>
              )}
            </div>
            <div className="px-2 pt-2 rounded border mt-2">
              {imagePreview.length > 0 && (
                <div className="d-flex mb-2 gap-2">
                  {imagePreview.map((image, index) => (
                    <div
                      key={index}
                      style={{
                        width: "10vw",
                        height: "10vh",
                        overflow: "hidden",
                        flexShrink: 0,
                      }}
                      className="mb-2 position-relative"
                    >
                      <img
                        src={image}
                        className="w-100 h-100"
                        style={{
                          objectFit: "cover", // keep aspect ratio, no stretching
                          display: "block",
                        }}
                        onError={() => {
                          setImagePreview((prev) =>
                            prev.filter((_, i) => i !== index)
                          );
                          setFile((prev) => prev.filter((_, i) => i !== index));
                        }}
                      />

                      <i
                        className="position-absolute bi bi-x-circle-fill text-danger fs-5 cursor-pointer"
                        onClick={() => {
                          setImagePreview((prev) =>
                            prev.filter((_, i) => i !== index)
                          );
                          setFile((prev) => prev.filter((_, i) => i !== index));
                        }}
                        style={{ top: "0px", left: "5px" }}
                      />
                    </div>
                  ))}
                </div>
              )}
              <Formik
                initialValues={{
                  content: "",
                }}
                innerRef={formikRef}
                enableReinitialize={true}
                onSubmit={createComment}
              >
                {({
                  values,
                  handleSubmit,
                  setFieldValue,
                  touched,
                  setTouched,
                  errors,
                  setFieldError,
                }) => (
                  <Form>
                    <Field
                      name="content"
                      component={TextArea}
                      placeholder={intl.formatMessage({ id: "comment" })}
                      autoComplete="off"
                      inputClass="form-control"
                      isAlphanumericOnly={false}
                      rows={1}
                    />
                  </Form>
                )}
              </Formik>
              <div className="d-flex justify-content-between align-items-center px-1">
                <input
                  type="file"
                  className="d-none"
                  placeholder={intl.formatMessage({ id: "image" })}
                  autoComplete="off"
                  accept="image/*"
                  onChange={handleFileChange}
                  ref={fileInputRef}
                />
                <i
                  className="bi bi-card-image text-primary fs-4 mt-1"
                  style={{ cursor: "pointer" }}
                  onClick={() => fileInputRef.current?.click()}
                />
                <button
                  type="button"
                  className="btn btn-primary py-0"
                  onClick={() => formikRef.current?.handleSubmit()}
                >
                  {intl.formatMessage({ id: "create" })}
                </button>
              </div>
            </div>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};
